import React, { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"
import Button from "@react/shared/ui/Button"

const formatAmountDecimal = (cents, symbol = "€") => {
  const value = (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${value} ${symbol}`
}

const FIELD_SLOT   = "ml-auto flex items-center gap-1.5 shrink-0"
const FIELD_INPUT  = "w-28 h-7 px-2 text-[12px] text-right border border-slate-200 rounded tabular-nums focus:border-slate-900 focus:ring-0 outline-none"
const FIELD_SUFFIX = "w-3 text-[11px] text-slate-400"

const SummaryPane = ({
  selectedServices,
  nextSituationNumber,
  guaranteeEnabled = true,
  guaranteePct,
  onGuaranteeChange,
  bankGuarantee,
  downPayment,
  downPaymentDeductedCents = 0,
  onDownPaymentDeductedChange,
  progressPct = 0,
  onBillSelection,
  onBillAll,
  submitting,
  currencySymbol,
}) => {
  const bankActive    = !!bankGuarantee?.enabled
  const dpExists      = !!downPayment?.exists
  const dpRemaining   = downPayment?.remaining_cents || 0
  const dpDeductCents = Math.max(0, Math.min(dpRemaining, downPaymentDeductedCents || 0))
  // Per Sophie Boulard: down payment is typically amortised between 20% and 80%
  // of project advancement. Outside that window we surface a soft warning
  // (but the user can still proceed — it's a guideline, not a hard rule).
  const dpAmortWindow      = { from: 20, to: 80 }
  const dpOutsideWindow    = dpDeductCents > 0 && (progressPct < dpAmortWindow.from || progressPct > dpAmortWindow.to)

  const totals = useMemo(() => {
    const htCents = selectedServices.reduce((acc, service) => acc + service.toBillCents, 0)
    const vatBreakdown = {}
    selectedServices.forEach(service => {
      const pct = Number(service.vat_pct) || 0
      vatBreakdown[pct] = (vatBreakdown[pct] || 0) + service.toBillCents
    })
    const vatCents = Object.entries(vatBreakdown).reduce((acc, [pct, base]) => acc + Math.round((base * Number(pct)) / 100), 0)
    const worksTtcCents = htCents + vatCents

    const effectiveVatRate  = htCents ? vatCents / htCents : 0
    const dpVatCents        = Math.round(dpDeductCents * effectiveVatRate)
    const netHtCents        = htCents - dpDeductCents
    const netVatCents       = vatCents - dpVatCents
    const ttcCents          = netHtCents + netVatCents

    const effectiveGuaranteePct = (!guaranteeEnabled || bankActive) ? 0 : Number(guaranteePct || 0)
    const guaranteeCents = Math.round((worksTtcCents * effectiveGuaranteePct) / 100)
    const netCents = ttcCents - guaranteeCents

    return { htCents, vatCents, worksTtcCents, netHtCents, netVatCents, ttcCents, dpVatCents, guaranteeCents, netCents, vatBreakdown }
  }, [selectedServices, guaranteePct, dpDeductCents, bankActive])

  return (
    <div className="lg:sticky lg:top-4 space-y-4">
      <Card className="overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
              {I18n.t("projects.situations.summary.eyebrow")}
            </div>
            <span className="text-[11px] text-slate-500 tabular-nums">{I18n.t("projects.situations.summary.draft")}</span>
          </div>
          <h3 className="text-[18px] font-semibold text-slate-900 tabular-nums">
            {I18n.t("projects.situations.summary.number", { number: nextSituationNumber })}
          </h3>
        </div>

        <dl className="px-5 py-4 space-y-2 text-[13px]">
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">{I18n.t("projects.situations.summary.lines_selected")}</dt>
            <dd className="tabular-nums font-medium">
              {I18n.t("projects.situations.summary.lines_count", { count: selectedServices.length })}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">{I18n.t("projects.situations.summary.total_ht")}</dt>
            <dd className="tabular-nums font-semibold text-slate-900">{formatAmountDecimal(totals.htCents, currencySymbol)}</dd>
          </div>

          {dpExists && (
            <div className="flex items-center justify-between gap-3">
              <dt className="text-slate-600 flex items-center gap-2 flex-1 min-w-0">
                <span className="truncate">{I18n.t("projects.situations.summary.down_payment_deduction")}</span>
                <span className={FIELD_SLOT}>
                  <input
                    type      = "number"
                    min       = "0"
                    max       = {dpRemaining / 100}
                    step      = "0.01"
                    value     = {(dpDeductCents / 100).toString()}
                    onChange  = {event => {
                      const n = Number(event.target.value)
                      if (!onDownPaymentDeductedChange) return
                      onDownPaymentDeductedChange(isNaN(n) ? 0 : Math.round(n * 100))
                    }}
                    className = {FIELD_INPUT}
                  />
                  <span className={FIELD_SUFFIX}>{currencySymbol || "€"}</span>
                </span>
              </dt>
              <dd className="tabular-nums text-slate-700 shrink-0">−{formatAmountDecimal(dpDeductCents, currencySymbol)}</dd>
            </div>
          )}
          {dpExists && (
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <span>{I18n.t("projects.situations.summary.down_payment_remaining")}</span>
              <span className="tabular-nums">{formatAmountDecimal(dpRemaining, currencySymbol)}</span>
            </div>
          )}

          {dpExists && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">{I18n.t("projects.situations.summary.net_ht")}</dt>
              <dd className="tabular-nums font-semibold text-slate-900">{formatAmountDecimal(totals.netHtCents, currencySymbol)}</dd>
            </div>
          )}

          <div className="border-t border-slate-100 my-2" />

          {/* Le détail par taux vaut pour les travaux du mois. Dès qu'un acompte
              est repris, il emporte sa TVA et le détail ne s'additionnerait
              plus : on affiche alors la TVA nette, celle de la facture. */}
          {Object.entries(totals.vatBreakdown).length === 0 ? (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">{I18n.t("projects.situations.summary.vat")}</dt>
              <dd className="tabular-nums text-slate-700">—</dd>
            </div>
          ) : dpExists ? (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">{I18n.t("projects.situations.summary.vat")}</dt>
              <dd className="tabular-nums text-slate-700">{formatAmountDecimal(totals.netVatCents, currencySymbol)}</dd>
            </div>
          ) : (
            Object.entries(totals.vatBreakdown).map(([pct, base]) => (
              <div key={pct} className="flex items-center justify-between">
                <dt className="text-slate-600 flex items-center gap-1.5">
                  {I18n.t("projects.situations.summary.vat")}
                  <span className="text-[11px] text-slate-400 tabular-nums">{pct}%</span>
                </dt>
                <dd className="tabular-nums text-slate-700">
                  {formatAmountDecimal(Math.round((base * Number(pct)) / 100), currencySymbol)}
                </dd>
              </div>
            ))
          )}

          <div className="flex items-center justify-between">
            <dt className="font-medium">{I18n.t("projects.situations.summary.total_ttc")}</dt>
            <dd className="tabular-nums font-semibold">{formatAmountDecimal(totals.ttcCents, currencySymbol)}</dd>
          </div>

          {guaranteeEnabled && <div className="border-t border-slate-100 my-2" />}

          {guaranteeEnabled && (bankActive ? (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600 flex items-center gap-1.5">
                {I18n.t("projects.situations.summary.guarantee")}
                <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-50 ring-1 ring-inset ring-blue-200 px-1.5 py-0.5 rounded">
                  {I18n.t("projects.situations.summary.bank_guarantee_active")}
                </span>
              </dt>
              <dd className="tabular-nums text-slate-400">—</dd>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <dt className="text-slate-600 flex items-center gap-2 flex-1 min-w-0">
                <span className="truncate">{I18n.t("projects.situations.summary.guarantee")}</span>
                <span className={FIELD_SLOT}>
                  <input
                    type      = "number"
                    min       = "0"
                    max       = "100"
                    value     = {guaranteePct}
                    onChange  = {event => onGuaranteeChange(Number(event.target.value))}
                    className = {FIELD_INPUT}
                  />
                  <span className={FIELD_SUFFIX}>%</span>
                </span>
              </dt>
              <dd className="tabular-nums text-slate-700 shrink-0">−{formatAmountDecimal(totals.guaranteeCents, currencySymbol)}</dd>
            </div>
          ))}

          {dpExists && dpOutsideWindow && (
            <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <span className="shrink-0 mt-0.5">⚠</span>
              <span>
                {I18n.t("projects.situations.summary.down_payment_window_warning", {
                  from:     dpAmortWindow.from,
                  to:       dpAmortWindow.to,
                  current:  progressPct,
                })}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <dt className="text-[14px] font-semibold">{I18n.t("projects.situations.summary.net_to_pay")}</dt>
            <dd className="tabular-nums text-[16px] font-semibold text-slate-900">{formatAmountDecimal(totals.netCents, currencySymbol)}</dd>
          </div>
        </dl>

        <div className="px-5 pb-5 space-y-2">
          <Button variant="primary" onClick={onBillSelection} disabled={submitting || selectedServices.length === 0} className="w-full justify-center">
            <FontAwesomeIcon icon={submitting ? "spinner" : "file-invoice"} spin={submitting} />
            {I18n.t("projects.situations.summary.bill_selection")}
          </Button>
          <Button variant="secondary" onClick={onBillAll} disabled={submitting} className="w-full justify-center">
            <FontAwesomeIcon icon="list-check" />
            {I18n.t("projects.situations.summary.bill_all")}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default SummaryPane
